import React, { ReactNode } from 'react';

import { useTheme } from '@mui/material';

import MuiAppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';

import { useUserStateContext } from '@/shared/components/UserStateProvider';

import AnimatedCosmLogo from '../AnimatedCosmLogo';
import { useNavigate } from 'react-router-dom';

type Props = {
  navigationAnchorEl: HTMLElement | null;
  setNavigationAnchorEl: (anchorEl: HTMLElement | null) => unknown;
  title: string;
  renderSummaryData?: () => ReactNode;
};

// see https://mui.com/material-ui/react-app-bar/#fixed-placement
export const AppBarSpacer = () => (
  <Box
    sx={(theme) => ({
      ...theme.mixins.toolbar,
      marginBottom: 1,
    })}
  />
);

const AppBar = ({ setNavigationAnchorEl, title, renderSummaryData = undefined }: Props) => {
  const { currentOrg } = useUserStateContext();

  const theme = useTheme();
  const navigate = useNavigate();

  // event handlers
  const handleNavigationOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setNavigationAnchorEl(event.currentTarget);
  };

  return (
    <MuiAppBar
      position="relative"
      sx={(sxTheme) => ({
        backgroundColor: 'transparent',
        backgroundImage: 'none',
        backdropFilter: 'blur(4.2px)',
        boxShadow: 'none',
        overflow: 'hidden',
        transition: sxTheme.transitions.create(['margin', 'width'], {
          easing: sxTheme.transitions.easing.sharp,
          duration: sxTheme.transitions.duration.leavingScreen,
        }),
      })}
    >
      <Toolbar
        disableGutters
        sx={{
          transition: `background 0.5s ease`,
          backgroundColor: 'transparent',
          padding: 0,
          minHeight: 'auto',
          display: 'flex',
          alignItems: 'stretch',
        }}
      >
        {/* Left section - Logo and Navigation */}
        <Box
          sx={{
            width: 'fit-content',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '6px',
            backgroundColor: theme.palette.cx.bg.surface,
            padding: theme.spacing(1),
            margin: theme.spacing(1),
          }}
        >
          <IconButton
            size="large"
            color="inherit"
            aria-label="menu"
            onClick={handleNavigationOpen}
            sx={{
              '&:hover': { backgroundColor: 'transparent' },
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M20.25 16.75C20.5261 16.75 20.75 16.9739 20.75 17.25C20.75 17.5261 20.5261 17.75 20.25 17.75H3.75C3.47386 17.75 3.25 17.5261 3.25 17.25C3.25 16.9739 3.47386 16.75 3.75 16.75H20.25ZM20.25 11.5C20.5261 11.5 20.75 11.7239 20.75 12C20.75 12.2761 20.5261 12.5 20.25 12.5H3.75C3.47386 12.5 3.25 12.2761 3.25 12C3.25 11.7239 3.47386 11.5 3.75 11.5H20.25ZM20.25 6.25C20.5261 6.25 20.75 6.47386 20.75 6.75C20.75 7.02614 20.5261 7.25 20.25 7.25H3.75C3.47386 7.25 3.25 7.02614 3.25 6.75C3.25 6.47386 3.47386 6.25 3.75 6.25H20.25Z"
                fill="white"
              />
            </svg>
          </IconButton>
        </Box>
        <Box
          sx={{
            width: 'fit-content',
            display: 'flex',
            alignItems: 'center',
            borderRadius: '6px',
            backgroundColor: theme.palette.cx.bg.surface,
            padding: theme.spacing(2),
            my: theme.spacing(1),
            mr: 1,
            '&:hover': { cursor: 'pointer' },
          }}
          onClick={async () => {
            await navigate('/games');
          }}
        >
          <AnimatedCosmLogo logoUrl={currentOrg?.logoPath ?? undefined} height={30} />
        </Box>

        {/* Right section - Title/Scoreboard and Menu */}
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            alignItems: 'center',
            padding: '8px 0px',
            marginRight: '8px',
          }}
        >
          {!renderSummaryData && (
            <Toolbar
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexGrow: 1,
                minWidth: 0,
                backgroundColor: theme.palette.cx.bg.surface,
                borderRadius: 1,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  flexGrow: 1,
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                }}
              >
                {title}
              </Typography>
            </Toolbar>
          )}

          {!!renderSummaryData && renderSummaryData()}
        </Box>
      </Toolbar>
    </MuiAppBar>
  );
};

export default AppBar;
